<template>
  <div class="recent-announcement-widget">
    <h3>Company Announcements</h3>
    <hr class="divider" />
    <div v-if="loading" class="recent-announcement-loading">Loading...</div>
    <div v-else-if="error" class="recent-announcement-error">{{ error }}</div>
    <div v-else-if="announcement">
      <LightboxLink
        :post="announcement"
        type="announcement"
        tag="a"
        btnClass="recent-announcement-link"
      >
        <div class="recent-announcement-icon">
          <i class="fa-sharp fa-light fa-megaphone"></i>
        </div>
        <div class="recent-announcement-content">
          <div class="recent-announcement-title">
            <h5><strong>{{ announcement.title.rendered }}</strong></h5>
          </div>
          <div
            v-if="announcement.content && announcement.content.rendered"
            v-html="truncateHtml(announcement.content.rendered, 200)"
            class="recent-announcement-excerpt"
          ></div>
          <span class="read-more">
            Read More <i class="fas fa-chevron-right" style="margin-bottom: 1px; font-size: 75%;"></i>
          </span>
        </div>
      </LightboxLink>
    </div>
    <div v-else class="recent-announcement-none">No recent announcements.</div>
  </div>
</template>

<script>
import LightboxLink from './LightboxLink.vue';
import { fetchAnnouncements, fetchCurrentUser, fetchCategoriesBySlugs, category_from_role } from '../utils/fetch.js';

const GENERAL_CATEGORY_ID = 1;

export default {
  name: "RecentAnnouncement",
  components: {
    LightboxLink
  },
  data() {
    return {
      announcement: null,
      loading: false,
      error: null,
      userCategoryIds: [],
    };
  },
  methods: {
    async fetchRecentGeneralAnnouncement() {
      this.loading = true;
      this.error = null;
      const CACHE_KEY = 'recentAnnouncementCache';
      const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes

      // Try cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { announcement, userCategoryIds, timestamp } = JSON.parse(cached);
        // Only use cache if not expired and user categories match
        if (
          Date.now() - timestamp < CACHE_EXPIRY &&
          JSON.stringify(this.userCategoryIds) === JSON.stringify(userCategoryIds)
        ) {
          this.announcement = announcement;
          this.loading = false;
          return;
        }
      }

      try {
        // Get user and allowed category IDs
        const user = await fetchCurrentUser();
        const roles = user.my_roles || [];
        const roleCategorySlugs = roles.map(role => category_from_role[role]).filter(Boolean);

        this.userCategoryIds = roleCategorySlugs.length
          ? (await fetchCategoriesBySlugs(roleCategorySlugs)).map(cat => cat.id)
          : [];

        // Fetch recent announcements (limit 5 for filtering)
        const posts = await fetchAnnouncements("&per_page=5&_embed");

        // Filter announcements based on categories
        const filtered = posts.filter(this.isAnnouncementAllowed);

        // Use the most recent allowed announcement
        this.announcement = filtered[0] || null;

        // Cache the result with userCategoryIds
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            announcement: this.announcement,
            userCategoryIds: this.userCategoryIds,
            timestamp: Date.now()
          })
        );
      } catch (err) {
        this.error = err.message || "Failed to fetch announcement.";
      } finally {
        this.loading = false;
      }
    },
    isAnnouncementAllowed(post) {
      // Treat as general if categories is missing, null, not an array, or empty
      if (!Array.isArray(post.categories) || post.categories.length === 0) {
        return true;
      }
      // Show if post is in the "general" category
      if (post.categories.includes(GENERAL_CATEGORY_ID)) {
        return true;
      }
      // Otherwise, check if any category matches user's allowed categories
      return post.categories.some(catId => this.userCategoryIds.includes(catId));
    },
    truncateHtml(html, limit) {
      const div = document.createElement('div');
      div.innerHTML = html || '';
      const text = div.textContent || div.innerText || '';
      if (text.length <= limit) return html;
      let truncated = text.slice(0, limit) + '...';
      return `<p>${truncated}</p>`;
    },
    async fetchData() {
      const CACHE_KEY = 'myDataCache';
      const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes

      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_EXPIRY) {
          this.myData = data;
          return;
        }
      }

    }
  },
  mounted() {
    this.fetchRecentGeneralAnnouncement();
    this.fetchData();
  }
};
</script>

<style>
h3 {
  text-align: left;
  font-size: 20px;
}
h5 {
  font-size: 16px;
  margin: 0;
}
.recent-announcement-widget {
  display: flex;
  flex-direction: column;
  border-radius: 30px;
  padding: 1.5rem;
  width: 100%;
  background: var(--blue-grad-left);
  color: #fff;
  text-align: center;
  min-height: 250px;
  box-sizing: border-box;
}
.recent-announcement-link {
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: flex-start;
  margin-top: 1rem;
  text-align: left;
  box-sizing: border-box;
}
.recent-announcement-icon {
  width: 80px;
  height: 80px;
  min-width: 80px;
  min-height: 80px;
  border-radius: 50%;
  background: rgba(255,255,255,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  font-size: 2.5rem;
  color: #fff;
  box-sizing: border-box;
}
.recent-announcement-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
}
.recent-announcement-title {
  font-weight: bold;
  color: #fff;
  text-decoration: none;
}
.recent-announcement-excerpt {
  font-size: 0.97em;
  margin: 0.5em 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Remove margin from p tags injected by v-html */
.recent-announcement-excerpt >>> p,
.recent-announcement-excerpt ::v-deep p {
  margin: 0;
}
.read-more {
  width: fit-content;
  margin-top: auto;
  background: none;
  border: none;
  color: #ffd600;
  text-decoration: none;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  font-size: 1em;
  transition: color 0.2s;
  display: inline-block;
}
.read-more:hover {
  color: #fff176;
}
.recent-announcement-loading,
.recent-announcement-error,
.recent-announcement-none {
  padding: 1rem 0;
  text-align: center;
}
hr.divider {
  border: 0;
  border-top: 1px solid #fff;
  margin: 5px 0;
}
</style>